import { loadParticleCoverageStats } from './particleCoverageService';
import { knowledgeService } from './knowledgeService';
import { reviewService } from './reviewService';
import { normalizeSubjectCode, type SubjectCode } from '../utils/subjectConfig';

export const HOME_PARTICLE_SUBJECT_ORDER: SubjectCode[] = ['math', 'yuwen', 'english'];

/** Client-side TTL: home banner stats change slowly; avoid re-hitting FC on every home focus. */
const HOME_COLLECTION_STATS_TTL_MS = 3 * 60 * 1000;

export type HomeCollectionSubjectParticleCount = {
  subject: SubjectCode;
  count: number;
};

export type HomeCollectionSubjectStat = {
  subject: SubjectCode;
  particleCount: number;
  reviewCount: number;
  coveragePercent: number;
  reviewedParticles: number;
};

export type HomeCollectionStats = {
  particleTotal: number;
  reviewCount: number;
  coveragePercent: number;
  reviewedParticles: number;
  particlesBySubject: HomeCollectionSubjectParticleCount[];
  bySubject: HomeCollectionSubjectStat[];
};

const homeCollectionStatsCache = new Map<string, { expiresAt: number; stats: HomeCollectionStats }>();

function cloneHomeCollectionStats(stats: HomeCollectionStats): HomeCollectionStats {
  return {
    particleTotal: stats.particleTotal,
    reviewCount: stats.reviewCount,
    coveragePercent: stats.coveragePercent,
    reviewedParticles: stats.reviewedParticles,
    particlesBySubject: stats.particlesBySubject.map((entry) => ({ ...entry })),
    bySubject: stats.bySubject.map((entry) => ({ ...entry })),
  };
}

export function invalidateHomeCollectionStats(userId?: string) {
  if (!userId) {
    homeCollectionStatsCache.clear();
    return;
  }
  homeCollectionStatsCache.delete(String(userId).trim());
}

export function peekHomeCollectionStats(userId: string): HomeCollectionStats | null {
  const cached = homeCollectionStatsCache.get(String(userId || '').trim());
  if (!cached || cached.expiresAt <= Date.now()) {
    return null;
  }
  return cloneHomeCollectionStats(cached.stats);
}

export function resolveActiveSubjectParticleCount(
  stats: HomeCollectionStats | null | undefined,
  activeSubject: SubjectCode,
): number {
  const row = stats?.bySubject.find((entry) => entry.subject === activeSubject);
  if (row) {
    return row.particleCount;
  }
  return stats?.particlesBySubject.find((entry) => entry.subject === activeSubject)?.count ?? 0;
}

export function resolveActiveSubjectReviewCount(
  stats: HomeCollectionStats | null | undefined,
  activeSubject: SubjectCode,
): number {
  return stats?.bySubject.find((entry) => entry.subject === activeSubject)?.reviewCount ?? 0;
}

export function resolveActiveSubjectCoveragePercent(
  stats: HomeCollectionStats | null | undefined,
  activeSubject: SubjectCode,
): number {
  return stats?.bySubject.find((entry) => entry.subject === activeSubject)?.coveragePercent ?? 0;
}

export async function loadHomeCollectionStats(
  userId: string,
  options?: { forceRefresh?: boolean },
): Promise<HomeCollectionStats> {
  const normalizedUserId = String(userId || '').trim();
  if (!options?.forceRefresh) {
    const cached = homeCollectionStatsCache.get(normalizedUserId);
    if (cached && cached.expiresAt > Date.now()) {
      return cloneHomeCollectionStats(cached.stats);
    }
  }

  const [particlesPage, history, coverage] = await Promise.all([
    knowledgeService.listKnowledgeParticles({
      userId: normalizedUserId,
      page: 1,
      pageSize: 1,
    }),
    reviewService.loadSessionHistory(normalizedUserId, {
      limit: 180,
      forceRefresh: options?.forceRefresh,
    }),
    loadParticleCoverageStats(normalizedUserId),
  ]);

  const completedHistory = history.filter((entry) => entry.status === 'completed');
  const historyCountBySubject = new Map<SubjectCode, number>();
  for (const code of HOME_PARTICLE_SUBJECT_ORDER) {
    historyCountBySubject.set(code, 0);
  }
  for (const entry of completedHistory) {
    const code = normalizeSubjectCode(entry.subject);
    if (code === 'other' || !HOME_PARTICLE_SUBJECT_ORDER.includes(code)) {
      continue;
    }
    historyCountBySubject.set(code, (historyCountBySubject.get(code) || 0) + 1);
  }

  const bySubject: HomeCollectionSubjectStat[] = HOME_PARTICLE_SUBJECT_ORDER.map((subject) => {
    const entry = coverage.bySubject.find((row) => row.subject === subject);
    const particleCount = entry?.totalParticles ?? 0;
    const reviewedParticles = entry?.reviewedParticles ?? 0;
    const coveragePercent = entry?.coveragePercent
      ?? (particleCount > 0 ? Math.min(100, Math.round((reviewedParticles / particleCount) * 100)) : 0);
    const sessionFromCoverage = entry?.sessionCount ?? 0;
    const sessionFromHistory = historyCountBySubject.get(subject) || 0;
    return {
      subject,
      particleCount,
      reviewedParticles,
      coveragePercent,
      reviewCount: sessionFromCoverage > 0 ? sessionFromCoverage : sessionFromHistory,
    };
  });

  const particlesBySubject = bySubject.map((entry) => ({
    subject: entry.subject,
    count: entry.particleCount,
  }));

  const stats: HomeCollectionStats = {
    particleTotal: particlesPage.pagination.total,
    reviewCount: completedHistory.length,
    coveragePercent: coverage.overall.coveragePercent,
    reviewedParticles: coverage.overall.reviewedParticles,
    particlesBySubject,
    bySubject,
  };

  homeCollectionStatsCache.set(normalizedUserId, {
    expiresAt: Date.now() + HOME_COLLECTION_STATS_TTL_MS,
    stats: cloneHomeCollectionStats(stats),
  });

  return stats;
}
